import React, { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

interface PostHogProviderWrapperProps {
  children: React.ReactNode;
  apiKey: string;
  options?: {
    host?: string;
    enableSessionReplay?: boolean;
  };
  autocapture?: boolean;
}

/**
 * Web-safe PostHog provider wrapper
 * On native platforms, wraps children with PostHogProvider from posthog-react-native
 * On web, initializes posthog-js SDK
 */
export function PostHogProviderWrapper({ 
  children, 
  apiKey, 
  options, 
  autocapture 
}: PostHogProviderWrapperProps) {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (Platform.OS === 'web' && !initializedRef.current) {
      initializedRef.current = true;
      // Dynamically import posthog-js for web
      import('posthog-js').then((posthogModule) => {
        const posthog = posthogModule.default;
        posthog.init(apiKey, {
          api_host: options?.host || 'https://us.i.posthog.com',
          person_profiles: 'identified_only',
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: autocapture ?? true,
          session_recording: options?.enableSessionReplay ? {
            maskAllInputs: false,
            maskInputOptions: {
              password: true,
            },
          } : undefined,
        });
        // Store globally for use in other parts of the app (both global and window for compatibility)
        (global as any).posthog = posthog;
        if (typeof window !== 'undefined') {
          (window as any).posthog = posthog;
        }
        console.log('✅ [POSTHOG] Web PostHog initialized');
      }).catch((err) => {
        console.warn('[PostHogProviderWrapper] posthog-js not available:', err);
      });
    }
  }, [apiKey, options, autocapture]);

  if (Platform.OS === 'web') {
    return <>{children}</>;
  }

  // On native, use PostHogProvider from posthog-react-native
  try {
    const { PostHogProvider } = require('posthog-react-native');
    return (
      <PostHogProvider
        apiKey={apiKey}
        options={options}
        autocapture={autocapture}
      >
        {children}
      </PostHogProvider>
    );
  } catch (e) {
    console.warn('[PostHogProviderWrapper] posthog-react-native not available, rendering children directly');
    return <>{children}</>;
  }
}

export default PostHogProviderWrapper;

